import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import API from "../../../services";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/store";
import {
  formatWlBalance,
  getWhitelabelDetailViewMode,
  getWhitelabelItemStatusRaw,
  getWlFailureReason,
  getWlPublicUrl,
  normalizeWlStatus,
  parseWhitelabelLiveDetailsResponse,
  type WhitelabelDetailViewMode,
  type WhitelabelListItemFields,
  type WhitelabelLiveDetails,
  whitelabelExternalHref,
} from "./whitelabelUi";
import WhitelabelSupportChatPopup from "./WhitelabelSupportChatPopup";
import { parseWhitelabelPackagePaymentMethods } from "./WhitelabelPackagePicker";
import SupportIcon from "../../../assets/images/support.svg";

type PendingPackage = {
  packageId?: string;
  name?: string;
  pointsCredited?: number;
  payAmount?: number;
  discountAmount?: number;
  currency?: string;
};

/** Draft-backed details model for pending/success/failed whitelabel states. */
type PendingDetails = WhitelabelListItemFields & {
  isDraft?: boolean;
  /** API draft/list display status (human-readable or code from `status`). */
  status?: string;
  package?: PendingPackage;
  selectedPackage?: PendingPackage;
  /** Display name from API `skinName`. */
  skinName?: string;
  /** Saved skin id from draft / submitted data when API supplies it. */
  skinId?: string;
  logoUrl?: string;
  logoType?: string;
  /** Raw logo payload when API stores base64 / data URL on `logo` instead of `logoUrl`. */
  logo?: string;
  loaderUrl?: string;
  loaderType?: string;
  loader?: string;
  selectedCategories?: string[] | string;
  managePayments?: boolean;
  enableFiatTransactions?: boolean;
  enableCryptoTransactions?: boolean;
  fiatBankSource?: string | null;
  onboardingSupportTicketId?: string;
  pointsCredited?: number | string;
  points?: number | string;
  /** Total price from API `totalAmount`. */
  totalAmount?: number | string;
  /** Already paid; optional on API — otherwise derived as `totalAmount − amountToBePaid`. */
  paidAmount?: number | string;
  /** Balance left to pay from API `amountToBePaid`. */
  amountToBePaid?: number | string;
  /** Draft discount amount from API `discountAmount`. */
  discountAmount?: number | string;
  /** Same object as POST `/whitelabel/create` body `themeConfig` when the draft API returns it. */
  themeConfig?: Record<string, string>;
  /** Selected theme id saved in draft. */
  themeId?: string;
  /** Selected theme display name saved in draft when provided by API. */
  themeName?: string;
  /** From draft/package row — forwarded to resume payment so checkout can enable FIAT/CRYPTO/etc. */
  paymentMethods?: string[];
  /** Non-empty payment note from `/whitelabel/draft` (`paymentRemark`). */
  paymentRemark?: string;
};

const toOptNumber = (x: unknown): number | undefined => {
  if (x === null || x === undefined || x === "") return undefined;
  const n = Number(x);
  return Number.isFinite(n) ? n : undefined;
};

const parsePendingPackageFromMerged = (
  merged: Record<string, unknown>
): PendingPackage | undefined => {
  const rawPkg = merged.package ?? merged.selectedPackage;
  if (!rawPkg || typeof rawPkg !== "object" || Array.isArray(rawPkg)) return undefined;
  const rp = rawPkg as Record<string, unknown>;
  const pts = toOptNumber(rp.pointsCredited ?? rp.points);
  const pay = toOptNumber(rp.payAmount);
  const discount = toOptNumber(rp.discountAmount);
  const pkg: PendingPackage = {
    packageId: String(rp.packageId ?? "").trim() || undefined,
    name: String(rp.name ?? rp.packageName ?? "").trim() || undefined,
    pointsCredited: pts,
    payAmount: pay,
    discountAmount: discount,
    currency: String(rp.currency ?? rp.currencyCode ?? "INR").trim(),
  };
  if (!pkg.name && !pkg.packageId && pkg.payAmount == null) {
    return undefined;
  }
  return pkg;
};

const formatDisplayBrandName = (raw: string): string => {
  if (!raw || raw === "—") return raw;
  return raw
    .trim()
    .split(/\s+/)
    .map((word) =>
      word.length ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ""
    )
    .filter(Boolean)
    .join(" ");
};

const formatDomainHref = (raw: string): string => {
  const t = String(raw || "").trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return whitelabelExternalHref(t);
  return whitelabelExternalHref(`https://${t.replace(/^\/+/, "")}`);
};

const normalizeLogoMimeType = (rawType: string): string => {
  const t = String(rawType || "").trim().toLowerCase();
  if (!t) return "image/png";
  if (t.startsWith("image/")) return t;
  if (t === "jpg") return "image/jpeg";
  if (t === "svg") return "image/svg+xml";
  return `image/${t}`;
};

const normalizeLogoCandidateToSrc = (candidate: string, logoType?: string): string => {
  const raw = String(candidate || "").trim();
  if (!raw) return "";
  if (/^(data:image\/|https?:\/\/|blob:)/i.test(raw)) return raw;
  if (/^\/\//.test(raw)) return `https:${raw}`;

  // API can send plain base64 without any data URL prefix.
  const sanitized = raw.replace(/\s+/g, "");
  if (/^[a-z0-9+/]+=*$/i.test(sanitized)) {
    const mime = normalizeLogoMimeType(logoType ?? "");
    return `data:${mime};base64,${sanitized}`;
  }

  return raw;
};

const resolveDraftLogoSrc = (details: PendingDetails): string => {
  const fromLogoUrl = normalizeLogoCandidateToSrc(details.logoUrl ?? "", details.logoType);
  if (fromLogoUrl) return fromLogoUrl;
  return normalizeLogoCandidateToSrc(details.logo ?? "", details.logoType);
};

const formatDraftMoney = (n: number | undefined, currency: string): string => {
  if (n === undefined || !Number.isFinite(n)) return "—";
  const rounded = Math.round(n);
  try {
    return new Intl.NumberFormat(currency === "INR" ? "en-IN" : undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(rounded);
  } catch {
    return `${rounded} ${currency}`;
  }
};

const formatPaymentOptionsSummary = (d: PendingDetails): string => {
  const parts: string[] = [];
  if (d.enableCryptoTransactions === true) parts.push("Crypto");
  if (d.enableFiatTransactions === true) parts.push("Fiat");
  return parts.length ? parts.join(" · ") : "—";
};

const normalizeThemeConfigRaw = (raw: unknown): Record<string, string> | undefined => {
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return undefined;
    try {
      return normalizeThemeConfigRaw(JSON.parse(t));
    } catch {
      return undefined;
    }
  }
  if (typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (v === null || v === undefined) continue;
    const s =
      typeof v === "string"
        ? v.trim()
        : typeof v === "number" && Number.isFinite(v)
          ? String(v)
          : String(v).trim();
    if (s) out[k] = s;
  }
  return Object.keys(out).length ? out : undefined;
};

/** `themeConfig` may live on the draft object or the envelope next to `draft`. */
const pickThemeConfigFromDraftSources = (
  draftRec: Record<string, unknown>,
  payloadRec: Record<string, unknown>
): Record<string, string> | undefined => {
  const candidates = [
    draftRec.themeConfig,
    payloadRec.themeConfig,
  ];
  for (const c of candidates) {
    const norm = normalizeThemeConfigRaw(c);
    if (norm && Object.keys(norm).length) return norm;
  }
  return undefined;
};

/** Build create-style `themeConfig` from legacy flat color fields on draft/submitted merge. */
const synthesizeThemeConfigFromLegacyFlat = (
  merged: Record<string, unknown>
): Record<string, string> | undefined => {
  const s = (x: unknown) => String(x ?? "").trim();
  const pc = s(merged.primaryColor);
  const sc = s(merged.secondaryColor);
  const hc = s(merged.headerColor);
  const bc = s(merged.backgroundColor);
  const out: Record<string, string> = {};
  if (pc) {
    out.primary = pc;
    out.border = pc;
  }
  if (sc) {
    out.teamInfoBackground = sc;
    out.inputBoxBackground = sc;
  }
  if (bc) {
    out.background = bc;
    out.teamInfoPrimary = bc;
  }
  if (hc) {
    out.tableBackground = hc;
    out.tableHeader = hc;
  }
  return Object.keys(out).length ? out : undefined;
};

/** Same slot mapping as create flow: API `themeConfig` → four UI colors. */
type WlDraftFourPalette = {
  primary: string;
  secondary: string;
  header: string;
  background: string;
};

const themeConfigToFourPalette = (
  tc: Record<string, string> | undefined
): WlDraftFourPalette | null => {
  if (!tc || !Object.keys(tc).length) return null;
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = String(tc[k] ?? "").trim();
      if (v) return v;
    }
    return "";
  };
  const primary = pick("primary", "primary_color") || pick("border", "border_color");
  const secondary =
    pick("teamInfoBackground") ||
    pick("inputBoxBackground");
  const background = pick("background") || pick("teamInfoPrimary");
  const header = pick("tableBackground") || pick("tableHeader");
  if (!primary && !secondary && !header && !background) return null;
  return {
    primary: primary || "—",
    secondary: secondary || "—",
    header: header || "—",
    background: background || "—",
  };
};

const isLikelyCssColor = (s: string): boolean => {
  const t = s.trim();
  if (!t || t === "—") return false;
  if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(t)) return true;
  if (/^(rgb|hsl)a?\(/i.test(t)) return true;
  return false;
};

const formatHexForDisplay = (s: string): string => {
  if (!s || s === "—") return "—";
  const t = s.trim();
  if (/^#[0-9a-f]{3,8}$/i.test(t)) return t.toLowerCase();
  return t;
};

const WhitelabelDraftSummaryCards = ({
  details,
  onPay,
  paymentPendingOverride,
}: {
  details: PendingDetails;
  onPay?: () => void;
  paymentPendingOverride?: boolean;
}) => {
  const brandName = String(details.whitelabelName || "").trim() || "—";
  const brandDisplay = formatDisplayBrandName(brandName);
  const domainRaw = String(details.subdomain || "").trim();
  const domainHref = domainRaw ? formatDomainHref(domainRaw) : "";
  const domainLabel = domainRaw
    ? /^https?:\/\//i.test(domainRaw)
      ? domainRaw
      : `https://${domainRaw.replace(/^\/+/, "")}`
    : "—";
  const statusLabel = String(details.status ?? "").trim() || "—";
  const logoSrc = resolveDraftLogoSrc(details);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);

  useEffect(() => {
    setLogoLoadFailed(false);
  }, [logoSrc]);

  const categories: string[] = Array.isArray(details.selectedCategories)
    ? details.selectedCategories.map((x) => String(x).trim()).filter(Boolean)
    : String(details.selectedCategories || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

  const skin = String(details.skinName ?? "").trim() || "—";
  const theme = String(details.themeName ?? "").trim() || String(details.themeId ?? "").trim() || "—";
  const paymentLine = formatPaymentOptionsSummary(details);

  const pkg = details.package ?? details.selectedPackage;
  const pkgName = String(details.packageName ?? "").trim() || "—";
  const ptsVal =
    toOptNumber(pkg?.pointsCredited) ??
    toOptNumber(details.pointsCredited) ??
    toOptNumber(details.points);
  const cur = String(pkg?.currency || "INR").trim() || "INR";
  const totalNum = toOptNumber(details.totalAmount);
  const dueNum = toOptNumber(details.amountToBePaid);
  const paidExplicit = toOptNumber(details.paidAmount);
  const paidNum =
    paidExplicit !== undefined
      ? paidExplicit
      : totalNum !== undefined && dueNum !== undefined
        ? totalNum - dueNum
        : undefined;
  const totalStr = formatDraftMoney(totalNum, cur);
  const paidStr = formatDraftMoney(paidNum, cur);
  const dueStr = formatDraftMoney(dueNum, cur);
  const amountDuePositive = dueNum !== undefined && dueNum > 0;
  const pendingPaymentTrue =
    typeof paymentPendingOverride === "boolean"
      ? paymentPendingOverride
      : details.paymentPending === true;
  const showPayCta = amountDuePositive && pendingPaymentTrue && Boolean(onPay);
  const showPaymentContactNotice = amountDuePositive && !pendingPaymentTrue;
  const ptsStr =
    ptsVal !== undefined
      ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(ptsVal)
      : "";

  const cardClass =
    "rounded-xl border border-border-color/30 bg-inputbox-color p-4 shadow-[0_0_0_1px_rgba(255,255,255,0.018)]";

  const fourPalette = useMemo(
    () => themeConfigToFourPalette(details.themeConfig),
    [details.themeConfig]
  );

  const themeColorInputClass =
    "min-w-0 flex-1 rounded-md border border-border-color bg-black/35 px-2 py-1.5 font-mono text-xs text-white outline-none";

  return (
    <div className="space-y-3">
      <div className={cardClass}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xl font-semibold text-white sm:text-2xl">{brandDisplay}</p>
            {domainHref ? (
              <a
                href={domainHref}
                target="_blank"
                rel="noreferrer"
                className="mt-0.5 block truncate text-sm font-medium text-[var(--border-color)] underline decoration-[color-mix(in_srgb,var(--border-color)_45%,transparent)] underline-offset-2 hover:opacity-90"
              >
                {domainLabel}
              </a>
            ) : (
              <p className="mt-0.5 text-sm text-white/55">—</p>
            )}
          </div>
          <div className="shrink-0">
            <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-amber-400/35 bg-amber-950/25 px-2.5 py-1 text-xs font-medium text-amber-200">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-300" aria-hidden />
              {statusLabel}
            </span>
          </div>
        </div>
        {logoSrc ? (
          <div className="mt-3 w-full rounded-md border border-white/10 bg-black/15 p-0">
            <div className="flex items-center gap-3">
              <span className="shrink-0 pl-1 text-[11px] font-medium leading-none text-white/55">Logo</span>
              <div className="flex h-24 min-w-0 flex-1 items-center justify-center overflow-hidden rounded-md border border-white/15 bg-black/25 p-1 sm:h-28">
                {logoLoadFailed ? (
                  <span className="px-1 text-center text-[10px] font-medium text-white/55">No logo</span>
                ) : (
                  <img
                    src={logoSrc}
                    alt="Brand logo"
                    className="h-full w-full object-contain object-center"
                    onError={() => setLogoLoadFailed(true)}
                  />
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className={cardClass}>
        <p className="text-sm font-bold uppercase tracking-wider text-white sm:text-base">
          Configuration
        </p>
        <div className="mt-1 divide-y divide-border-color/25">
          <div className="flex items-start justify-between gap-3 py-2.5">
            <span className="text-xs text-white/50">Skin</span>
            <span className="max-w-[65%] text-right text-sm text-white">{skin}</span>
          </div>
          <div className="flex items-start justify-between gap-3 py-2.5">
            <span className="text-xs text-white/50">Theme</span>
            <span className="max-w-[65%] text-right text-sm text-white">{theme}</span>
          </div>
          <div className="flex items-start justify-between gap-3 py-2.5">
            <span className="shrink-0 text-xs text-white/50">Categories</span>
            <div className="flex max-w-[70%] flex-wrap justify-end gap-1.5">
              {categories.length ? (
                categories.map((c) => (
                  <span
                    key={c}
                    className="rounded-md border border-border-color/30 bg-border-color/25 px-2 py-0.5 text-xs text-white/90"
                  >
                    {c}
                  </span>
                ))
              ) : (
                <span className="text-sm text-white/55">—</span>
              )}
            </div>
          </div>
          <div className="flex items-start justify-between gap-3 py-2.5">
            <span className="text-xs text-white/50">Payment options</span>
            <span className="max-w-[70%] text-right text-sm text-white">{paymentLine}</span>
          </div>
          {fourPalette ? (
            <div className="border-t border-border-color/25 pt-3">
              <p className="mb-2 text-xs font-semibold tracking-wide text-white/80">Theme</p>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {(
                  [
                    { label: "Primary", value: fourPalette.primary },
                    { label: "Secondary", value: fourPalette.secondary },
                    { label: "Header", value: fourPalette.header },
                    { label: "Background", value: fourPalette.background },
                  ] as const
                ).map(({ label, value }) => {
                  const display = formatHexForDisplay(value);
                  const showSwatch = isLikelyCssColor(display);
                  return (
                    <div key={label} className="min-w-0 space-y-1">
                      <span className="text-[11px] font-medium text-white/75">{label}</span>
                      <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
                        <span
                          className="h-5 w-5 shrink-0 rounded border border-white/20 shadow-inner sm:h-6 sm:w-6"
                          style={
                            showSwatch
                              ? { backgroundColor: display }
                              : { backgroundColor: "rgba(255,255,255,0.06)" }
                          }
                          title={display}
                          aria-hidden
                        />
                        <input
                          type="text"
                          readOnly
                          value={display}
                          aria-label={`${label} color`}
                          className={themeColorInputClass}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className={cardClass}>
        <p className="text-sm font-bold uppercase tracking-wider text-white sm:text-base">
          Package
        </p>
        <div className="mt-1 divide-y divide-border-color/25">
          <div className="flex items-start justify-between gap-3 py-2.5">
            <span className="text-xs text-white/50">Package</span>
            <span className="max-w-[70%] text-right text-sm font-medium text-white">
              {pkgName}
            </span>
          </div>
          <div className="flex items-start justify-between gap-3 py-2.5">
            <span className="text-xs text-white/50">Points</span>
            <span className="text-right text-sm tabular-nums text-white">
              {ptsStr || "—"}
            </span>
          </div>
          <div className="flex items-start justify-between gap-3 py-2.5">
            <span className="text-xs text-white/50">Total amount</span>
            <span
              className={`text-right text-sm tabular-nums text-white ${
                totalStr !== "—" ? "font-medium" : "text-white/55"
              }`}
            >
              {totalStr}
            </span>
          </div>
          <div className="flex items-start justify-between gap-3 py-2.5">
            <span className="text-xs text-white/50">Paid</span>
            <span
              className={`text-right text-sm tabular-nums text-white ${
                paidStr !== "—" ? "font-medium" : "text-white/55"
              }`}
            >
              {paidStr}
            </span>
          </div>
          <div className="flex items-start justify-between gap-3 py-2.5">
            <span className="text-xs text-white/50">Amount to pay</span>
            <span
              className={`text-right tabular-nums text-white ${
                dueStr !== "—" ? "text-xl font-semibold tracking-tight sm:text-2xl" : "text-sm text-white/55"
              }`}
            >
              {dueStr}
            </span>
          </div>
        </div>
        {showPayCta || showPaymentContactNotice ? (
          <div className="mt-1.5 space-y-2 border-t border-border-color/25 pt-2">
            {showPaymentContactNotice ? (
              <div
                className="rounded-md border border-amber-500/40 bg-amber-950/30 px-2.5 py-1.5 text-xs leading-snug text-amber-100/95 sm:text-sm"
                role="status"
              >
                Please contact us to complete your payment.
              </div>
            ) : null}
            {showPayCta && onPay ? (
              <button
                type="button"
                onClick={onPay}
                className="w-full rounded-md border border-border-color/30 bg-border-color px-3 py-2.5 text-sm font-semibold text-white hover:opacity-95"
              >
                Pay
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const normalizeCategoryList = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (item && typeof item === "object") {
        const obj = item as Record<string, unknown>;
        return String(obj.label ?? obj.name ?? obj.key ?? "").trim();
      }
      return "";
    })
    .filter(Boolean);
};

const normalizeBool = (x: unknown): boolean | null => {
  if (x === true || x === "true" || x === 1 || x === "1") return true;
  if (x === false || x === "false" || x === 0 || x === "0") return false;
  return null;
};

/** Non-draft live row: explicit `isDraft === false`, or `SUCCESS` when the flag is omitted. */
const shouldEnrichWithLiveDetails = (d: PendingDetails): boolean => {
  const b = normalizeBool(d.isDraft);
  if (b === false) return true;
  if (b === true) return false;
  return normalizeWlStatus(getWhitelabelItemStatusRaw(d)) === "SUCCESS";
};

/** `location.state.listPrefetch` from list page after a successful details/draft API call. */
type WhitelabelDetailsListPrefetch = {
  kind: "active" | "pending";
  /** Axios `response.data` from `/whitelabel/get-whitelabel-details` or `/whitelabel/draft`. */
  data: unknown;
};

const mapActiveWhitelabelDetailsEnvelope = (
  wlId: string,
  packageIdFromQuery: string,
  data: unknown
): { details: PendingDetails; liveDetails: WhitelabelLiveDetails | null } | null => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const root = data as Record<string, unknown>;
  if (root.status !== "RS_OK") return null;
  const payload = root.whitelabel;
  const detailsRec =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : {};
  const rid = String(detailsRec.whitelabelId ?? "").trim();
  if (rid && rid !== wlId) return null;
  const mapped: PendingDetails = {
    whitelabelId: wlId,
    isDraft: false,
    whitelabelStatus: "SUCCESS",
    packageId: packageIdFromQuery || undefined,
    balance:
      detailsRec.balance === null ||
      detailsRec.balance === undefined ||
      String(detailsRec.balance).trim() === ""
        ? undefined
        : (detailsRec.balance as number | string),
    publicUrl: String(detailsRec.webUrl ?? "").trim() || undefined,
  };
  return { details: mapped, liveDetails: parseWhitelabelLiveDetailsResponse(data) };
};

const mapDraftEnvelopeToPendingDetails = (
  wlId: string,
  packageIdFromQuery: string,
  data: unknown
): PendingDetails | null => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const root = data as Record<string, unknown>;
  if (root.status !== "RS_OK") return null;
  const payload = root.data && typeof root.data === "object" ? root.data : root;
  const payloadObj = payload as Record<string, unknown>;
  const draft =
    payloadObj?.draft && typeof payloadObj.draft === "object"
      ? payloadObj.draft
      : payloadObj;
  const draftRec =
    draft && typeof draft === "object" && !Array.isArray(draft)
      ? (draft as Record<string, unknown>)
      : {};
  const draftId = String(draftRec.whitelabelId ?? "").trim();
  if (draftId && draftId !== wlId) return null;
  const payloadRec =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : {};
  const domainValue = String(draftRec.brandDomain ?? draftRec.subdomain ?? "").trim();
  const brandNameValue = String(draftRec.brandName ?? draftRec.whitelabelName ?? "").trim();
  const skinNameValue = String(draftRec.skinName ?? "").trim();
  const skinIdValue = String(draftRec.skinId ?? "").trim();
  const selectedCategoriesValue =
    normalizeCategoryList(draftRec.categories ?? draftRec.selectedCategories) || [];
  const isDraftFlag = normalizeBool(draftRec.isDraft);
  const statusRaw = draftRec.status;
  const d = {
    whitelabelId: String(draftRec.whitelabelId ?? wlId).trim(),
    isDraft: isDraftFlag === null ? undefined : isDraftFlag,
    status:
      statusRaw !== undefined && statusRaw !== null
        ? String(statusRaw).trim() || undefined
        : undefined,
    whitelabelName: brandNameValue,
    subdomain: domainValue,
    whitelabelStatus: String(draftRec.whitelabelStatus ?? "").trim(),
    paymentPending: draftRec.paymentPending ?? draftRec.payment_pending,
    paymentCompleted: draftRec.paymentCompleted ?? draftRec.payment_completed,
    packageId: draftRec.packageId ?? packageIdFromQuery,
    packageName: String(draftRec.packageName ?? "").trim() || undefined,
    skinName: skinNameValue || undefined,
    skinId: skinIdValue || undefined,
    selectedCategories: selectedCategoriesValue,
    managePayments: draftRec.managePayments,
    enableFiatTransactions: draftRec.enableFiatTransactions,
    enableCryptoTransactions: draftRec.enableCryptoTransactions,
    fiatBankSource: draftRec.fiatBankSource ?? null,
    balance: draftRec.balance,
    pointsCredited: draftRec.pointsCredited ?? draftRec.points,
    points: draftRec.points,
    failureReason: draftRec.failureReason ?? "",
    publicUrl: draftRec.publicUrl ?? "",
    onboardingSupportTicketId: String(draftRec.onboardingSupportTicketId ?? "").trim(),
    totalAmount: draftRec.totalAmount,
    paidAmount: draftRec.paidAmount,
    amountToBePaid: draftRec.amountToBePaid,
    discountAmount: draftRec.discountAmount,
    logoUrl: String(draftRec.logoUrl ?? "").trim() || undefined,
    logoType: String(draftRec.logoType ?? "").trim() || undefined,
    loaderUrl: String(draftRec.loaderUrl ?? "").trim() || undefined,
    loaderType: String(draftRec.loaderType ?? "").trim() || undefined,
    logo: String(draftRec.logo ?? "").trim() || undefined,
    loader: String(draftRec.loader ?? "").trim() || undefined,
    themeId: String(draftRec.themeId ?? "").trim() || undefined,
    themeName: String(draftRec.themeName ?? "").trim() || undefined,
  } as PendingDetails;
  const mergedForPkg: Record<string, unknown> = { ...draftRec };
  let themeCfg = pickThemeConfigFromDraftSources(draftRec, payloadRec);
  if (!themeCfg) themeCfg = synthesizeThemeConfigFromLegacyFlat(mergedForPkg);
  if (themeCfg) d.themeConfig = themeCfg;

  let selectedPackage = parsePendingPackageFromMerged(mergedForPkg);
  const pkgId = String(d.packageId ?? packageIdFromQuery ?? "").trim();
  const pkgNameFromRoot = String(mergedForPkg.packageName ?? "").trim();
  if (!selectedPackage && pkgId) {
    selectedPackage = { packageId: pkgId, name: pkgNameFromRoot || undefined };
  } else if (selectedPackage && !selectedPackage.name && pkgNameFromRoot) {
    selectedPackage = { ...selectedPackage, name: pkgNameFromRoot };
  }
  if (selectedPackage) {
    d.package = selectedPackage;
    d.selectedPackage = selectedPackage;
  }
  const pmFromNested = parseWhitelabelPackagePaymentMethods(
    mergedForPkg.package ?? mergedForPkg.selectedPackage
  );
  const pmFromRoot = parseWhitelabelPackagePaymentMethods(mergedForPkg);
  d.paymentMethods = pmFromNested && pmFromNested.length > 0 ? pmFromNested : pmFromRoot;
  const paymentRemarkRaw = String(draftRec.paymentRemark ?? "").trim();
  if (paymentRemarkRaw) d.paymentRemark = paymentRemarkRaw;
  return d;
};

const apiGetWhitelabelDraft = (payload: { userKey: string; whitelabelId: string }) => {
  return API.post("/whitelabel/draft", payload);
};

const apiDeleteDraftWhitelabel = (payload: { whitelabelId: string }) => {
  return API.post("/whitelabel/delete-draft", payload);
};

/** Live brand fields when `isDraft === false` (logo, URLs, balance). */
const apiGetWhitelabelDetails = (payload: { whitelabelId: string; userKey?: string }) => {
  const body: Record<string, string> = { whitelabelId: payload.whitelabelId };
  if (payload.userKey) body.userKey = payload.userKey;
  return API.post("/whitelabel/get-whitelabel-details", body);
};


type PortalAccessSectionProps = {
  live: WhitelabelLiveDetails;
  expanded: boolean;
  onToggleExpanded: () => void;
  showPassword: boolean;
  onTogglePassword: () => void;
  borderDividerClass: string;
};

const TransactionHistoryNavCard = ({
  onClick,
}: {
  onClick: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-2 w-full rounded-lg border border-white/[0.1] bg-transparent px-3 py-3 text-left transition-colors hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/35"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-sm font-semibold text-zinc-100">View transaction history</p>
        <ChevronRightRoundedIcon sx={{ fontSize: 20, color: "rgba(244, 244, 245, 0.85)" }} />
      </div>
    </button>
  );
};

const PortalAccessSection = ({
  live,
  expanded,
  onToggleExpanded,
  showPassword,
  onTogglePassword,
  borderDividerClass,
}: PortalAccessSectionProps) => {
  const portalUrl = String(live.portalUrl ?? "").trim();
  const portalUserId = String(live.portalUserId ?? "").trim();
  const portalPassword = String(live.portalPassword ?? "").trim();
  const launchHref = portalUrl ? whitelabelExternalHref(portalUrl) : "";

  const passwordDisplay =
    !portalPassword
      ? ""
      : showPassword
        ? portalPassword
        : "•".repeat(Math.min(portalPassword.length, 28));

  if (!portalUrl && !portalUserId && !portalPassword) return null;

  const showUserIdRow = Boolean(portalUserId);
  const showPasswordRow = Boolean(portalPassword);
  const userIdHasDividerBelow = showPasswordRow || Boolean(launchHref);
  const passwordHasDividerBelow = Boolean(launchHref);

  return (
    <div className={`border-t ${borderDividerClass}`}>
      <button
        type="button"
        onClick={onToggleExpanded}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-2 py-2 text-left hover:bg-white/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/35 rounded-sm"
      >
        <span className="text-sm font-semibold text-zinc-100">Portal details</span>
        <ExpandMoreIcon
          sx={{
            fontSize: 22,
            color: "rgba(244, 244, 245, 0.85)",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </button>
      {expanded ? (
        <div className="pb-1 pt-0.5">
          {(showUserIdRow || showPasswordRow || launchHref) && (
            <div className="overflow-hidden rounded-md border border-white/[0.1] bg-zinc-950/50">
              {showUserIdRow ? (
                <div
                  className={`grid grid-cols-[7.5rem_minmax(0,1fr)] items-center gap-x-3 px-3 py-2.5 ${userIdHasDividerBelow ? "border-b border-white/10" : ""}`}
                >
                  <span className="text-xs font-medium text-zinc-500">User ID</span>
                  <span className="min-w-0 break-all text-sm leading-snug text-zinc-200">
                    {portalUserId}
                  </span>
                </div>
              ) : null}
              {showPasswordRow ? (
                <div
                  className={`grid grid-cols-[7.5rem_minmax(0,1fr)_auto] items-center gap-x-3 px-3 py-2.5 ${passwordHasDividerBelow ? "border-b border-white/10" : ""}`}
                >
                  <span className="text-xs font-medium text-zinc-500">Password</span>
                  <span
                    className={`min-w-0 break-all text-sm leading-snug text-zinc-200 ${showPassword ? "" : "select-none"}`}
                  >
                    {passwordDisplay}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTogglePassword();
                    }}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    title={showPassword ? "Hide password" : "Show password"}
                    className="shrink-0 rounded p-1.5 text-zinc-400 hover:bg-white/10 hover:text-zinc-100 focus:outline-none"
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                  </button>
                </div>
              ) : null}
              {launchHref ? (
                <div className="px-3 py-2.5">
                  <a
                    href={launchHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-sky-400 underline decoration-sky-500/50 underline-offset-2 hover:text-sky-300"
                  >
                    Open portal
                    <OpenInNewIcon sx={{ fontSize: 18 }} aria-hidden />
                  </a>
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

const PendingWhitelabelDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { whitelabelId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const routeMode = String(searchParams.get("mode") ?? "").trim().toLowerCase();
  const isActiveRoute = routeMode === "active";
  const navState = location.state as {
    paymentPending?: unknown;
    listPrefetch?: WhitelabelDetailsListPrefetch;
  } | null;
  const userDetails = useSelector((state: RootState) => state.userDetails.userDetails);
  const packageIdFromQuery = String(searchParams.get("packageId") || "").trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<PendingDetails | null>(null);
  const [liveDetails, setLiveDetails] = useState<WhitelabelLiveDetails | null>(null);
  const [liveDetailsLoading, setLiveDetailsLoading] = useState(false);
  const [portalPasswordVisible, setPortalPasswordVisible] = useState(false);
  const [portalExpanded, setPortalExpanded] = useState(false);
  const [supportChatOpen, setSupportChatOpen] = useState(false);
  const [deletingDraft, setDeletingDraft] = useState(false);
  const listPaymentPending = useMemo(
    () => normalizeBool(navState?.paymentPending) === true,
    [navState?.paymentPending]
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  /** List page already loaded draft; skip follow-up `/get-whitelabel-details` enrichment. */
  const skipLiveDetailsEnrichmentApi = useMemo(
    () => navState?.listPrefetch?.kind === "pending",
    [navState?.listPrefetch?.kind],
  );

  useEffect(() => {
    setPortalPasswordVisible(false);
    setPortalExpanded(false);
  }, [
    whitelabelId,
    liveDetails?.portalPassword,
    liveDetails?.portalUserId,
    liveDetails?.portalUrl,
    details?.whitelabelId,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const wlId = String(whitelabelId || "").trim();
        if (!wlId) {
          if (!cancelled) {
            setDetails(null);
            setError("Whitelabel details are unavailable.");
          }
          return;
        }

        const pf = navState?.listPrefetch;
        if (pf?.data != null) {
          if (isActiveRoute && pf.kind === "active") {
            const mapped = mapActiveWhitelabelDetailsEnvelope(wlId, packageIdFromQuery, pf.data);
            if (mapped) {
              if (!cancelled) {
                setDetails(mapped.details);
                setLiveDetails(mapped.liveDetails);
              }
              return;
            }
          } else if (!isActiveRoute && pf.kind === "pending") {
            const d = mapDraftEnvelopeToPendingDetails(wlId, packageIdFromQuery, pf.data);
            if (d) {
              if (!cancelled) {
                setDetails(d);
                setLiveDetails(null);
              }
              return;
            }
          }
        }

        const userKey = String(userDetails?.UserKey || "").trim();
        if (isActiveRoute) {
          const response = await apiGetWhitelabelDetails({ whitelabelId: wlId });
          const data = response?.data;
          if (data?.status !== "RS_OK") {
            const msg = data?.errorDescription || "Error loading whitelabel details.";
            if (!cancelled) {
              setDetails(null);
              setError(msg);
              toast.error(msg);
            }
            return;
          }
          const mapped = mapActiveWhitelabelDetailsEnvelope(wlId, packageIdFromQuery, data);
          if (!mapped) {
            const msg = "Error loading whitelabel details.";
            if (!cancelled) {
              setDetails(null);
              setError(msg);
              toast.error(msg);
            }
            return;
          }
          if (!cancelled) {
            setDetails(mapped.details);
            setLiveDetails(mapped.liveDetails);
          }
          return;
        }

        if (!userKey) {
          if (!cancelled) {
            setDetails(null);
            setError("Whitelabel details are unavailable.");
          }
          return;
        }
        const response = await apiGetWhitelabelDraft({ userKey, whitelabelId: wlId });
        const data = response?.data;
        if (data?.status !== "RS_OK") {
          const msg = data?.errorDescription || "Error loading pending whitelabel details.";
          if (!cancelled) {
            setDetails(null);
            setError(msg);
            toast.error(msg);
          }
          return;
        }
        const d = mapDraftEnvelopeToPendingDetails(wlId, packageIdFromQuery, data);
        if (!d) {
          const msg = "Error loading pending whitelabel details.";
          if (!cancelled) {
            setDetails(null);
            setError(msg);
            toast.error(msg);
          }
          return;
        }
        if (!cancelled) setDetails(d);
      } catch (e: unknown) {
        const msg =
          (e as { response?: { data?: { errorDescription?: string } } })?.response?.data
            ?.errorDescription || "Error loading pending whitelabel details.";
        if (!cancelled) {
          setDetails(null);
          setError(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [whitelabelId, userDetails?.UserKey, packageIdFromQuery, isActiveRoute, navState?.listPrefetch]);

  useEffect(() => {
    let cancelled = false;
    if (isActiveRoute) {
      setLiveDetailsLoading(false);
      return;
    }
    if (skipLiveDetailsEnrichmentApi) {
      setLiveDetailsLoading(false);
      return;
    }
    if (!details) {
      setLiveDetails(null);
      setLiveDetailsLoading(false);
      return;
    }
    if (!shouldEnrichWithLiveDetails(details)) {
      setLiveDetails(null);
      setLiveDetailsLoading(false);
      return;
    }
    const wlId = String(details.whitelabelId ?? whitelabelId).trim();
    const userKey = String(userDetails?.UserKey || "").trim();

    if (!wlId || !userKey) {
      setLiveDetails(null);
      setLiveDetailsLoading(false);
      return;
    }

    setLiveDetailsLoading(true);
    setLiveDetails(null);
    (async () => {
      try {
        const response = await apiGetWhitelabelDetails({
          whitelabelId: wlId,
          ...(userKey ? { userKey } : {}),
        });
        const parsed = parseWhitelabelLiveDetailsResponse(response?.data);
        if (!cancelled) setLiveDetails(parsed ?? null);
      } catch {
        if (!cancelled) setLiveDetails(null);
      } finally {
        if (!cancelled) setLiveDetailsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [details, whitelabelId, userDetails?.UserKey, isActiveRoute, skipLiveDetailsEnrichmentApi]);

  const resolvedWhitelabelId = useMemo(
    () => String(details?.whitelabelId ?? whitelabelId).trim(),
    [details, whitelabelId]
  );
  const resolvedPackageId = useMemo(
    () => String(details?.packageId ?? packageIdFromQuery).trim(),
    [details, packageIdFromQuery]
  );

  const detailViewMode: WhitelabelDetailViewMode = useMemo(
    () => getWhitelabelDetailViewMode(details),
    [details]
  );

  const effectivePaymentPending = useMemo(() => {
    const fromDetails = normalizeBool(details?.paymentPending);
    if (fromDetails !== null) return fromDetails;
    const paidDone = normalizeBool(details?.paymentCompleted) === true;
    const dueNum =
      toOptNumber(details?.amountToBePaid) ??
      (() => {
        const totalNum = toOptNumber(details?.totalAmount);
        const paidNum = toOptNumber(details?.paidAmount);
        if (totalNum === undefined || paidNum === undefined) return undefined;
        return totalNum - paidNum;
      })();
    if (!paidDone && dueNum !== undefined && dueNum > 0) return true;
    return listPaymentPending;
  }, [
    details?.paymentPending,
    details?.paymentCompleted,
    details?.amountToBePaid,
    details?.totalAmount,
    details?.paidAmount,
    listPaymentPending,
  ]);

  const showEdit = detailViewMode !== "success" && effectivePaymentPending;
  const showDeleteDraft = effectivePaymentPending;

  const publicUrl = useMemo(() => {
    if (!details) return "";
    const live = liveDetails?.webUrl?.trim();
    if (live) return whitelabelExternalHref(live);
    return getWlPublicUrl(details);
  }, [details, liveDetails]);
  const failureReason = useMemo(
    () => (details ? getWlFailureReason(details) : ""),
    [details]
  );
  const balanceLabel = useMemo(() => {
    if (!details) return "-";
    const liveBal = liveDetails?.balance;
    if (liveBal != null && String(liveBal).trim() !== "") {
      return formatWlBalance({ ...details, balance: liveBal }, { integerDisplay: true });
    }
    return formatWlBalance(details, { integerDisplay: true });
  }, [details, liveDetails]);

  const telegramHref = useMemo(() => {
    const t = liveDetails?.telegramUrl?.trim();
    return t ? whitelabelExternalHref(t) : "";
  }, [liveDetails?.telegramUrl]);

  const showLiveEnrichment = details != null && shouldEnrichWithLiveDetails(details);

  const goToEdit = () => {
    const qs = new URLSearchParams();
    if (resolvedWhitelabelId) qs.set("whitelabelId", resolvedWhitelabelId);
    qs.set("edit", "1");
    const selectedCategoriesRaw = details?.selectedCategories;
    const categories =
      Array.isArray(selectedCategoriesRaw)
        ? selectedCategoriesRaw
        : String(selectedCategoriesRaw || "")
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean);
    const editPalette = themeConfigToFourPalette(details?.themeConfig);
    const storedTc = details?.themeConfig;
    const hasStoredThemeConfig =
      Boolean(storedTc) &&
      typeof storedTc === "object" &&
      Object.keys(storedTc as Record<string, string>).length > 0;
    const themeConfigForPrefill = hasStoredThemeConfig
      ? { ...(storedTc as Record<string, string>) }
      : undefined;

    navigate(`/whitelabel/create?${qs.toString()}`, {
      state: {
        prefillData: {
          brandName: details?.whitelabelName ?? "",
          brandDomain: details?.subdomain ?? "",
          skinId: details?.skinId ?? "",
          skinName: details?.skinName ?? "",
          categories,
          packageId: details?.packageId ?? resolvedPackageId,
          managePayments: details?.managePayments,
          enableFiatTransactions: details?.enableFiatTransactions,
          enableCryptoTransactions: details?.enableCryptoTransactions,
          fiatBankSource: details?.fiatBankSource ?? null,
          whitelabelStatus: String(details?.whitelabelStatus ?? "IN_PROGRESS"),
          logoUrl: details?.logoUrl ?? "",
          logoType: details?.logoType ?? "",
          logo: details?.logo ?? "",
          loaderUrl: details?.loaderUrl ?? "",
          loaderType: details?.loaderType ?? "",
          loader: details?.loader ?? "",
          themeId: details?.themeId ?? "",
          ...(themeConfigForPrefill ? { themeConfig: themeConfigForPrefill } : {}),
          ...(editPalette && editPalette.primary !== "—"
            ? { primaryColor: editPalette.primary }
            : {}),
          ...(editPalette && editPalette.secondary !== "—"
            ? { secondaryColor: editPalette.secondary }
            : {}),
          ...(editPalette && editPalette.header !== "—" ? { headerColor: editPalette.header } : {}),
          ...(editPalette && editPalette.background !== "—"
            ? { backgroundColor: editPalette.background }
            : {}),
        },
      },
    });
  };

  const goToPayment = () => {
    const qs = new URLSearchParams();
    if (resolvedWhitelabelId) qs.set("whitelabelId", resolvedWhitelabelId);
    if (resolvedPackageId) qs.set("packageId", resolvedPackageId);
    qs.set("resumePayment", "1");
    const packageObj = details?.package ?? details?.selectedPackage ?? {};
    const paymentMethodsForPrefill =
      details?.paymentMethods?.length ? details.paymentMethods : parseWhitelabelPackagePaymentMethods(packageObj);
    const pointsForPrefill =
      toOptNumber(packageObj?.pointsCredited) ??
      toOptNumber(details?.pointsCredited) ??
      toOptNumber(details?.points) ??
      0;
    const payAmountForPrefill =
      toOptNumber(packageObj?.payAmount) ??
      toOptNumber(details?.amountToBePaid) ??
      toOptNumber(details?.totalAmount) ??
      0;
    const discountAmountForPrefill =
      toOptNumber(packageObj?.discountAmount) ??
      toOptNumber((details as Record<string, unknown> | null)?.discountAmount);
    navigate(`/whitelabel/create?${qs.toString()}`, {
      state: {
        paymentPrefillData: {
          whitelabelId: resolvedWhitelabelId,
          packageId: resolvedPackageId,
          packageName: details?.packageName ?? packageObj?.name ?? "",
          pointsCredited: pointsForPrefill,
          payAmount: payAmountForPrefill,
          ...(discountAmountForPrefill != null ? { discountAmount: discountAmountForPrefill } : {}),
          currency: packageObj?.currency ?? "INR",
          ...(paymentMethodsForPrefill?.length ? { paymentMethods: paymentMethodsForPrefill } : {}),
          draft: {
            whitelabelId: resolvedWhitelabelId,
            packageId: resolvedPackageId,
            packageName: details?.packageName ?? packageObj?.name ?? "",
            points: pointsForPrefill ?? 0,
            amountToBePaid: payAmountForPrefill ?? 0,
            discountAmount: discountAmountForPrefill ?? 0,
            currency: packageObj?.currency ?? "INR",
            ...(paymentMethodsForPrefill?.length ? { paymentMethods: paymentMethodsForPrefill } : {}),
          },
        },
      },
    });
  };

  const goAddFundsPackages = () => {
    navigate("/whitelabel/add-funds", {
      state: {
        returnTo: `${location.pathname}${location.search}`,
        whitelabelId: resolvedWhitelabelId,
      },
    });
  };

  const goToTransactions = () => {
    if (!resolvedWhitelabelId) {
      toast.error("Whitelabel id is missing.");
      return;
    }
    navigate(`/whitelabel/transactions?whitelabelId=${encodeURIComponent(resolvedWhitelabelId)}`, {
      state: { whitelabelId: resolvedWhitelabelId },
    });
  };

  const openDeleteDraftConfirm = () => {
    if (deletingDraft) return;
    const wlId = resolvedWhitelabelId;
    if (!wlId) {
      toast.error("Whitelabel id is missing.");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const deleteDraftWhitelabel = async () => {
    if (deletingDraft) return;
    const wlId = resolvedWhitelabelId;
    if (!wlId) {
      toast.error("Whitelabel id is missing.");
      return;
    }
    setDeletingDraft(true);
    try {
      const response = await apiDeleteDraftWhitelabel({ whitelabelId: wlId });
      const data = response?.data;
      if (data?.status !== "RS_OK") {
        const msg = data?.errorDescription || "Unable to delete draft whitelabel.";
        toast.error(msg);
        return;
      }
      setShowDeleteConfirm(false);
      toast.success("Draft whitelabel deleted.");
      navigate("/whitelabel", { replace: true });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { errorDescription?: string } } })?.response?.data
          ?.errorDescription || "Unable to delete draft whitelabel.";
      toast.error(msg);
    } finally {
      setDeletingDraft(false);
    }
  };

  const copyPublicUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied.");
    } catch {
      toast.error("Could not copy link.");
    }
  };

  const copyTelegramUrl = async () => {
    if (!telegramHref) return;
    try {
      await navigator.clipboard.writeText(telegramHref);
      toast.success("Telegram link copied.");
    } catch {
      toast.error("Could not copy link.");
    }
  };

  const draftSummary = details ? (
    <WhitelabelDraftSummaryCards
      details={details}
      onPay={detailViewMode === "inProgressPayPending" ? goToPayment : undefined}
      paymentPendingOverride={effectivePaymentPending}
    />
  ) : null;

  const paymentRemarkTrim = String(details?.paymentRemark ?? "").trim();

  return (
    <div className="text-white px-3 sm:px-4 py-[5rem] pb-[8rem]">
      {showDeleteConfirm ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 px-4"
          role="presentation"
          onClick={() => !deletingDraft && setShowDeleteConfirm(false)}
        >
          <div
            className="w-full max-w-[280px] rounded-xl border border-red-500/35 bg-zinc-950 p-4 shadow-[0_18px_48px_rgba(0,0,0,0.7)]"
            role="dialog"
            aria-modal="true"
            aria-label="Delete draft confirmation"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm leading-relaxed text-zinc-300">
              Delete this draft? Saved information will be lost.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deletingDraft}
                className="rounded-md border border-white/15 bg-transparent px-3 py-1.5 text-sm text-zinc-200 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={deleteDraftWhitelabel}
                disabled={deletingDraft}
                className="rounded-md border border-red-500/50 bg-red-500/15 px-3 py-1.5 text-sm font-semibold text-red-200 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingDraft ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="mx-auto max-w-4xl">
        <header className="mb-4 flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/whitelabel")}
              className="bg-inputbox-color flex h-8 w-8 shrink-0 items-center justify-center rounded"
              aria-label="Go back"
              title="Back"
            >
              <ArrowBackIcon
                className="text-white"
                style={{ width: "20px", height: "20px", color: "#FFFFFF" }}
              />
            </button>
            <h1 className="min-w-0 text-2xl font-semibold leading-tight text-zinc-100 sm:text-3xl">
              Details
            </h1>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {!loading && !error && details?.onboardingSupportTicketId ? (
              <button
                type="button"
                onClick={() => setSupportChatOpen(true)}
                className="inline-flex items-center justify-center rounded-md p-1.5 text-white transition-colors hover:bg-white/10"
                aria-label="Open customer support chat"
                title="Customer support"
              >
                <img
                  src={SupportIcon}
                  alt=""
                  className="h-6 w-6"
                  style={{
                    filter:
                      "invert(100%) sepia(100%) saturate(0%) hue-rotate(139deg) brightness(110%) contrast(101%)",
                  }}
                />
              </button>
            ) : null}
            {!loading && !error && showEdit ? (
              <button
                type="button"
                onClick={goToEdit}
                className="inline-flex items-center justify-center rounded-md p-1.5 text-white transition-colors hover:bg-white/10"
                aria-label="Edit whitelabel draft"
                title="Edit"
              >
                <EditRoundedIcon sx={{ fontSize: 24, color: "currentColor" }} />
              </button>
            ) : null}
            {!loading && !error && showDeleteDraft ? (
              <button
                type="button"
                onClick={openDeleteDraftConfirm}
                disabled={deletingDraft}
                className="inline-flex items-center justify-center rounded-md p-1.5 text-red-300 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60"
                aria-label={deletingDraft ? "Deleting draft whitelabel" : "Delete whitelabel draft"}
                title={deletingDraft ? "Deleting..." : "Delete draft"}
              >
                <DeleteOutlineRoundedIcon sx={{ fontSize: 24, color: "currentColor" }} />
              </button>
            ) : null}
          </div>
        </header>

        {!loading && !error && paymentRemarkTrim ? (
          <div
            className="mt-4 rounded-xl border border-amber-500/45 bg-amber-950/35 px-3 py-2.5 text-sm shadow-[0_0_0_1px_rgba(251,191,36,0.12)]"
            role="alert"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-200/95">
              Payment notice
            </p>
            <p className="mt-1.5 whitespace-pre-wrap leading-relaxed text-amber-50/95">
              {paymentRemarkTrim}
            </p>
          </div>
        ) : null}

        {loading ? (
          <p className="mt-8 text-center text-sm text-zinc-400">Loading...</p>
        ) : null}

        {!loading && !!error ? (
          <div className="mt-6 flex min-h-[45vh] flex-col items-center justify-center px-2">
            <p className="text-center text-sm text-amber-400 sm:text-base">
              Error loading whitelabel details
            </p>
          </div>
        ) : null}

        {!loading && !error && showLiveEnrichment && detailViewMode !== "success" ? (
          <div className="mt-6 space-y-5 rounded-xl border border-white/[0.08] bg-zinc-950/40 p-5">
            <p className="text-sm font-semibold text-zinc-300">Brand</p>
            {liveDetailsLoading ? (
              <p className="text-sm text-zinc-400">Loading brand details…</p>
            ) : null}
            <div className="rounded-xl border border-[color-mix(in_srgb,var(--border-color)_32%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_14%,transparent)] px-4 py-3.5">
              <div className="flex items-start justify-between gap-3">
                <p className="text-xs font-medium text-zinc-400">Balance</p>
                <button
                  type="button"
                  onClick={goAddFundsPackages}
                  className="shrink-0 rounded-md bg-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:opacity-95 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--border-color)_65%,white)] focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                >
                  Add funds
                </button>
              </div>
              <p className="mt-1 flex flex-wrap items-baseline gap-2">
                <span className="text-2xl font-bold tabular-nums tracking-tight text-white sm:text-3xl">
                  {balanceLabel}
                </span>
                <span className="text-sm font-medium text-zinc-400">points</span>
              </p>
            </div>
            {telegramHref ? (
              <div className="border-t border-white/[0.06] py-1.5">
                <div className="flex items-center gap-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-zinc-400">Telegram</p>
                    <p className="mt-0.5 break-all text-sm text-zinc-200">{telegramHref}</p>
                  </div>
                  <button
                    type="button"
                    onClick={copyTelegramUrl}
                    aria-label="Copy Telegram link"
                    title="Copy link"
                    className="shrink-0 rounded p-1 text-zinc-400 hover:bg-white/5 hover:text-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/35"
                  >
                    <ContentCopyRoundedIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </div>
            ) : null}
            {publicUrl ? (
              <div className="border-t border-white/[0.06] py-1.5">
                <div className="flex items-center gap-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-zinc-400">Website</p>
                    <p className="mt-0.5 break-all text-sm text-zinc-200">{publicUrl}</p>
                  </div>
                  <button
                    type="button"
                    onClick={copyPublicUrl}
                    aria-label="Copy website link"
                    title="Copy link"
                    className="shrink-0 rounded p-1 text-zinc-400 hover:bg-white/5 hover:text-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/35"
                  >
                    <ContentCopyRoundedIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </div>
            ) : !liveDetailsLoading ? (
              <p className="text-sm text-zinc-500">No website URL yet.</p>
            ) : null}
            {!liveDetailsLoading && liveDetails ? (
              <PortalAccessSection
                live={liveDetails}
                expanded={portalExpanded}
                onToggleExpanded={() => setPortalExpanded((v) => !v)}
                showPassword={portalPasswordVisible}
                onTogglePassword={() => setPortalPasswordVisible((v) => !v)}
                borderDividerClass="border-white/[0.06]"
              />
            ) : null}
            {isActiveRoute ? <TransactionHistoryNavCard onClick={goToTransactions} /> : null}
          </div>
        ) : null}

        {!loading && !error && details && detailViewMode === "success" ? (
          <div className="mt-6 space-y-5 rounded-xl border border-emerald-500/25 bg-emerald-950/20 p-5 shadow-[0_0_0_1px_rgba(255,255,255,0.04)]">
            {showLiveEnrichment && liveDetailsLoading ? (
              <p className="text-sm text-zinc-400">Loading brand details…</p>
            ) : null}
            <div className="rounded-xl border border-[color-mix(in_srgb,var(--border-color)_32%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_14%,transparent)] px-4 py-3.5">
              <div className="flex items-start justify-between gap-3">
                <p className="text-xs font-medium text-zinc-400">Balance</p>
                <button
                  type="button"
                  onClick={goAddFundsPackages}
                  className="shrink-0 rounded-md bg-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:opacity-95 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--border-color)_65%,white)] focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                >
                  Add funds
                </button>
              </div>
              <p className="mt-1 flex flex-wrap items-baseline gap-2">
                <span className="text-2xl font-bold tabular-nums tracking-tight text-white sm:text-3xl">
                  {balanceLabel}
                </span>
                <span className="text-sm font-medium text-zinc-400">points</span>
              </p>
            </div>
            {telegramHref ? (
              <div className="border-t border-white/[0.08] py-1.5">
                <div className="flex items-center gap-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-zinc-400">Telegram</p>
                    <p className="mt-0.5 break-all text-sm text-zinc-200">{telegramHref}</p>
                  </div>
                  <button
                    type="button"
                    onClick={copyTelegramUrl}
                    aria-label="Copy Telegram link"
                    title="Copy link"
                    className="shrink-0 rounded p-1 text-zinc-400 hover:bg-white/5 hover:text-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/35"
                  >
                    <ContentCopyRoundedIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </div>
            ) : null}
            {publicUrl ? (
              <div className="border-t border-white/[0.08] py-1.5">
                <div className="flex items-center gap-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-zinc-400">Website</p>
                    <p className="mt-0.5 break-all text-sm text-zinc-200">{publicUrl}</p>
                  </div>
                  <button
                    type="button"
                    onClick={copyPublicUrl}
                    aria-label="Copy website link"
                    title="Copy link"
                    className="shrink-0 rounded p-1 text-zinc-400 hover:bg-white/5 hover:text-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/35"
                  >
                    <ContentCopyRoundedIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-amber-200/90">No public URL was returned yet.</p>
            )}
            {!liveDetailsLoading && liveDetails ? (
              <PortalAccessSection
                live={liveDetails}
                expanded={portalExpanded}
                onToggleExpanded={() => setPortalExpanded((v) => !v)}
                showPassword={portalPasswordVisible}
                onTogglePassword={() => setPortalPasswordVisible((v) => !v)}
                borderDividerClass="border-white/[0.08]"
              />
            ) : null}
            {isActiveRoute ? <TransactionHistoryNavCard onClick={goToTransactions} /> : null}
          </div>
        ) : null}

        {!loading && !error && details && detailViewMode === "failed" ? (
          <div className="mt-6 space-y-4">
            <div
              className="rounded-lg border border-red-500/40 bg-red-950/20 p-4 text-sm text-red-100/95"
              role="alert"
            >
              <p className="font-semibold text-red-200">This request did not go through</p>
              {failureReason ? (
                <p className="mt-2 leading-relaxed text-red-100/90">{failureReason}</p>
              ) : (
                <p className="mt-2 text-red-200/80">No reason was provided. Try editing and resubmitting.</p>
              )}
            </div>
            <div className="rounded-lg border border-border-color bg-border-color/10 p-4 shadow-[0_0_0_1px_rgba(255,255,255,0.08)]">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Your draft
              </p>
              <div className="mt-1">{draftSummary}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={goToEdit}
                  className="rounded-md border border-border-color bg-border-color px-3 py-1.5 text-xs font-medium text-white hover:opacity-95"
                >
                  Edit and retry
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {!loading && !error && details && detailViewMode === "inProgressPayDone" ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg border border-border-color bg-border-color/15 p-3 text-sm text-white/85">
              Payment received. We&apos;re processing your whitelabel — you&apos;ll see updates here
              when the status changes.
            </div>
            <div className="rounded-lg border border-border-color bg-border-color/10 p-4 shadow-[0_0_0_1px_rgba(255,255,255,0.08)]">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/45">
                Submitted details
              </p>
              <div className="mt-1">{draftSummary}</div>
            </div>
          </div>
        ) : null}

        {!loading && !error && details && detailViewMode === "inProgressPayPending" ? (
          <div className="mt-6 rounded-lg border border-border-color bg-border-color/10 p-4 shadow-[0_0_0_1px_rgba(255,255,255,0.08)]">
            {draftSummary}
          </div>
        ) : null}
      </div>
      <WhitelabelSupportChatPopup
        isOpen={supportChatOpen}
        onClose={() => setSupportChatOpen(false)}
        ticketId={String(details?.onboardingSupportTicketId ?? "").trim()}
      />
    </div>
  );
};

export default PendingWhitelabelDetails;
